import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/shared/lib/supabase";
import { useAppContext } from "@/shared/hooks/useAppContext";

export interface UserEstateWithRole {
  estate_id: number;
  person: {
    estate_id: number;
    full_name: string | null;
    first_name: string | null;
    last_name: string | null;
    date_of_birth: string | null;
    date_of_death: string | null;
    main_image_url: string | null;
    funeral_home: string | null;
    location: string | null;
  } | null;
}

export function useUserEstates() {
  const { user } = useAppContext();

  const {
    data: userEstates,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["userEstates", user?.user_id],
    queryFn: async (): Promise<UserEstateWithRole[]> => {
      if (!user?.user_id) {
        return [];
      }

      // Get user_estates relationships (simple junction table)
      const { data: userEstatesData, error: userEstatesError } = await supabase
        .from("user_estates")
        .select("estate_id")
        .eq("user_id", user.user_id);

      if (userEstatesError) {
        throw new Error(`Failed to fetch user persons: ${userEstatesError.message}`);
      }

      if (!userEstatesData || userEstatesData.length === 0) {
        return [];
      }

      // Get person details for each estate_id
      const estateIds = userEstatesData.map((up) => up.estate_id);
      const { data: personsData, error: personsError } = await supabase
        .from("estates")
        .select(
          `
          estate_id,
          full_name,
          first_name,
          last_name,
          date_of_birth,
          date_of_death,
          main_image_url,
          funeral_home,
          location
        `,
        )
        .in("estate_id", estateIds);

      if (personsError) {
        throw new Error(`Failed to fetch persons: ${personsError.message}`);
      }

      // Return the person data for each person the user has access to
      return userEstatesData.map((userPerson) => ({
        estate_id: userPerson.estate_id,
        person: personsData?.find((p) => p.estate_id === userPerson.estate_id) || null,
      }));
    },
    enabled: !!user?.user_id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    userEstates: userEstates || [],
    isLoading,
    error,
    refetch,
  };
}
